// Resolves the subset of SVG presentation attributes and CSS the map actually
// uses into a plain inherited-style object the WebGL backend can consume.
//
// This is deliberately not a CSS engine. It covers exactly what `features/Map`
// emits: presentation attributes, a `style` attribute limited to a few
// properties, and the three classes in `styles/map.scss` that the schemas set.
// Anything outside that is ignored rather than guessed at.

// Class rules mirrored from `styles/map.scss`. Only the classes the schemas
// actually apply are listed; the rest of the `#map` block is dead as far as the
// imperative map code is concerned. Keep in sync with that stylesheet.
const CLASS_RULES = {
  whiteFill: { fill: "white" },
  whiteFade: { fill: "white", opacity: 0.75 },
  // The stylesheet also carries `letter-spacing: -0.5`, which has no unit and so
  // is not a valid CSS length — the browser drops it, and matching the SVG
  // renderer means dropping it here too.
  appName: { fontWeight: "bold" },
  whiteFilledBlackBorder: { fill: "#fff", stroke: "#000", strokeWidth: 0.5 },
  blackThinStrokeOnly: { stroke: "black", strokeWidth: 0.5, fill: "none" },
  centerText: { textAnchor: "middle", dominantBaseline: "middle" },
  centerRightText: { textAnchor: "end", dominantBaseline: "middle" },
  horizontalCenterText: { textAnchor: "middle" },
};

// Font used by any <text> that does not override it. Resolved from the live
// container once the map mounts, so the map keeps matching the page's font
// without hard-coding Semantic UI's stack here.
const baseFont = {
  fontFamily: "Lato, 'Helvetica Neue', Arial, Helvetica, sans-serif",
  fontSize: 16,
  fontWeight: "normal",
  fontStyle: "normal",
};

export function setBaseFontFrom(element) {
  if (typeof window === "undefined" || !element || !window.getComputedStyle)
    return;
  const cs = window.getComputedStyle(element);
  if (cs.fontFamily) baseFont.fontFamily = cs.fontFamily;
  const size = parseFloat(cs.fontSize);
  if (size > 0) baseFont.fontSize = size;
  if (cs.fontWeight) baseFont.fontWeight = cs.fontWeight;
  if (cs.fontStyle) baseFont.fontStyle = cs.fontStyle;
}

export const ROOT_STYLE = {
  fill: 0x000000ff,
  stroke: 0,
  strokeWidth: 1,
  strokeLinecap: "butt",
  strokeDasharray: null,
  opacity: 1,
  get fontFamily() {
    return baseFont.fontFamily;
  },
  get fontSize() {
    return baseFont.fontSize;
  },
  get fontWeight() {
    return baseFont.fontWeight;
  },
  get fontStyle() {
    return baseFont.fontStyle;
  },
  letterSpacing: 0,
  textAnchor: "start",
  dominantBaseline: "auto",
  blend: "normal",
  clip: null,
};

// ---------------------------------------------------------------------------
// Colour
// ---------------------------------------------------------------------------

// Colours are stored as a single 0xRRGGBBAA integer. Parsing goes through a 2d
// context because that resolves every syntax the schemas use — named colours,
// three- and six-digit hex, and the hsl() the scope hue animation produces —
// without this file carrying a colour table.
let colourCtx = null;
const colourCache = new Map();

export const COLOUR_NONE = 0;

export function parseColour(value) {
  if (value == null) return null;
  if (typeof value === "number") return value;

  const key = String(value);
  const cached = colourCache.get(key);
  if (cached !== undefined) return cached;

  const parsed = parseColourUncached(key);
  colourCache.set(key, parsed);
  return parsed;
}

function parseColourUncached(value) {
  const trimmed = value.trim().toLowerCase();
  if (trimmed === "none" || trimmed === "transparent" || trimmed === "")
    return COLOUR_NONE;
  if (trimmed === "black") return 0x000000ff;
  if (trimmed === "white") return 0xffffffff;

  // Fast path for the hex literals that dominate the schemas.
  if (trimmed[0] === "#") {
    if (trimmed.length === 7) {
      const n = parseInt(trimmed.slice(1), 16);
      if (!isNaN(n)) return (n * 256 + 255) >>> 0;
    } else if (trimmed.length === 4) {
      const r = trimmed[1];
      const g = trimmed[2];
      const b = trimmed[3];
      const n = parseInt(r + r + g + g + b + b, 16);
      if (!isNaN(n)) return (n * 256 + 255) >>> 0;
    }
  }

  if (typeof document === "undefined") return 0x000000ff;

  if (!colourCtx) {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 1;
    colourCtx = canvas.getContext("2d");
  }

  // An unparseable value leaves fillStyle at its previous setting, so it is reset
  // to a sentinel first and a failure to change is treated as "not a colour".
  colourCtx.fillStyle = "#000000";
  colourCtx.fillStyle = trimmed;
  const normalised = colourCtx.fillStyle;

  if (normalised[0] === "#") {
    const n = parseInt(normalised.slice(1), 16);
    return (n * 256 + 255) >>> 0;
  }

  const parts = normalised.match(/[\d.]+/g);
  if (!parts || parts.length < 3) return 0x000000ff;
  const r = Math.round(Number(parts[0]));
  const g = Math.round(Number(parts[1]));
  const b = Math.round(Number(parts[2]));
  const a = parts.length > 3 ? Math.round(Number(parts[3]) * 255) : 255;
  return ((r << 24) | (g << 16) | (b << 8) | a) >>> 0;
}

export function withAlpha(colour, factor) {
  if (factor >= 1) return colour;
  const a = Math.max(0, Math.min(255, Math.round((colour & 0xff) * factor)));
  return ((colour & 0xffffff00) | a) >>> 0;
}

// ---------------------------------------------------------------------------
// Lengths
// ---------------------------------------------------------------------------

export function parseLength(value, fallback, relativeTo) {
  if (value == null || value === "") return fallback;
  if (typeof value === "number") return value;

  const str = String(value).trim();
  const n = parseFloat(str);
  if (isNaN(n)) return fallback;

  if (str.endsWith("em")) return n * (relativeTo || 16);
  if (str.endsWith("pt")) return (n * 96) / 72;
  if (str.endsWith("%")) return (n / 100) * (relativeTo || 0);
  return n;
}

// ---------------------------------------------------------------------------
// Inline style attribute
// ---------------------------------------------------------------------------

const declarationCache = new Map();
const DECLARATION_CACHE_LIMIT = 512;

function parseDeclarations(text) {
  const cached = declarationCache.get(text);
  if (cached) return cached;

  const out = {};
  const parts = text.split(";");
  for (let i = 0; i < parts.length; i++) {
    const colon = parts[i].indexOf(":");
    if (colon < 0) continue;
    out[parts[i].slice(0, colon).trim().toLowerCase()] = parts[i]
      .slice(colon + 1)
      .trim();
  }

  if (declarationCache.size >= DECLARATION_CACHE_LIMIT)
    declarationCache.clear();
  declarationCache.set(text, out);
  return out;
}

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------

// Attributes that, when set on a node, change how its descendants render. The
// batch builder marks a whole subtree dirty on any change, so this is only used
// to decide whether a resolved style can be shared with children unchanged.
const INHERITED_DEFAULTS = {
  fill: true,
  stroke: true,
  strokeWidth: true,
  fontSize: true,
  fontFamily: true,
  fontWeight: true,
  textAnchor: true,
  dominantBaseline: true,
  letterSpacing: true,
};

function copyStyle(parent) {
  return {
    fill: parent.fill,
    stroke: parent.stroke,
    strokeWidth: parent.strokeWidth,
    strokeLinecap: parent.strokeLinecap,
    strokeDasharray: parent.strokeDasharray,
    opacity: parent.opacity,
    fontFamily: parent.fontFamily,
    fontSize: parent.fontSize,
    fontWeight: parent.fontWeight,
    fontStyle: parent.fontStyle,
    letterSpacing: parent.letterSpacing,
    textAnchor: parent.textAnchor,
    dominantBaseline: parent.dominantBaseline,
    // Not inherited in SVG: a blend mode or clip applies to the element it is set
    // on and its descendants as one group, which the batch builder handles by
    // carrying them down explicitly rather than through style resolution.
    blend: parent.blend,
    clip: parent.clip,
  };
}

// Returns the style a node renders with, given its parent's resolved style.
// Returns the parent object itself when the node changes nothing, which keeps
// the common case of a plain <g> allocation-free.
export function resolveStyle(node, parent) {
  const attrs = node.attrs;
  let style = null;

  const set = (key, value) => {
    if (!style) style = copyStyle(parent);
    style[key] = value;
  };

  const className = attrs["class"];
  if (className) {
    const names = className.split(/\s+/);
    for (let i = 0; i < names.length; i++) {
      const rule = CLASS_RULES[names[i]];
      if (!rule) continue;
      for (const key in rule) {
        if (key === "fill" || key === "stroke")
          set(key, parseColour(rule[key]));
        else set(key, rule[key]);
      }
    }
  }

  if (attrs.fill !== undefined) set("fill", parseColour(attrs.fill));
  if (attrs.stroke !== undefined) set("stroke", parseColour(attrs.stroke));
  if (attrs["stroke-width"] !== undefined)
    set("strokeWidth", parseLength(attrs["stroke-width"], parent.strokeWidth));
  if (attrs["stroke-linecap"] !== undefined)
    set("strokeLinecap", attrs["stroke-linecap"]);
  if (attrs["stroke-dasharray"] !== undefined)
    set("strokeDasharray", parseDashes(attrs["stroke-dasharray"]));
  if (attrs.opacity !== undefined) {
    const o = parseFloat(attrs.opacity);
    set("opacity", parent.opacity * (isNaN(o) ? 1 : o));
  }
  if (attrs["font-size"] !== undefined)
    set(
      "fontSize",
      parseLength(attrs["font-size"], parent.fontSize, parent.fontSize)
    );
  if (attrs["font-family"] !== undefined)
    set("fontFamily", attrs["font-family"]);
  if (attrs["font-weight"] !== undefined)
    set("fontWeight", attrs["font-weight"]);
  if (attrs["font-style"] !== undefined) set("fontStyle", attrs["font-style"]);
  if (attrs["letter-spacing"] !== undefined)
    set(
      "letterSpacing",
      parseLength(attrs["letter-spacing"], 0, parent.fontSize)
    );
  if (attrs["text-anchor"] !== undefined)
    set("textAnchor", attrs["text-anchor"]);
  if (attrs["dominant-baseline"] !== undefined)
    set("dominantBaseline", attrs["dominant-baseline"]);

  const styleAttr = attrs.style;
  if (styleAttr) {
    const decls = parseDeclarations(styleAttr);
    if (decls["font-size"] !== undefined)
      set(
        "fontSize",
        parseLength(decls["font-size"], parent.fontSize, parent.fontSize)
      );
    if (decls["font-weight"] !== undefined)
      set("fontWeight", decls["font-weight"]);
    if (decls["font-family"] !== undefined)
      set("fontFamily", decls["font-family"]);
    if (decls.fill !== undefined) set("fill", parseColour(decls.fill));
    if (decls.stroke !== undefined) set("stroke", parseColour(decls.stroke));
    if (decls.opacity !== undefined) {
      const o = parseFloat(decls.opacity);
      set("opacity", parent.opacity * (isNaN(o) ? 1 : o));
    }
    if (decls["mix-blend-mode"] !== undefined)
      set("blend", decls["mix-blend-mode"]);
    if (decls["letter-spacing"] !== undefined)
      set(
        "letterSpacing",
        parseLength(decls["letter-spacing"], 0, parent.fontSize)
      );
  }

  return style || parent;
}

// `display` and `visibility` are checked separately from style resolution: they
// prune the walk rather than contribute to it.
export function isHidden(node) {
  const attrs = node.attrs;
  if (attrs.display === "none") return true;
  if (attrs.visibility === "hidden" || attrs.visibility === "collapse")
    return true;
  const styleAttr = attrs.style;
  if (styleAttr && styleAttr.indexOf("display") >= 0) {
    const decls = parseDeclarations(styleAttr);
    if (decls.display === "none") return true;
  }
  return false;
}

function parseDashes(value) {
  if (!value || value === "none") return null;
  const parts = String(value)
    .split(/[\s,]+/)
    .map(Number)
    .filter((n) => !isNaN(n) && n >= 0);
  if (!parts.length) return null;
  // An odd-length list repeats to make the on/off pattern even, as in SVG.
  return parts.length % 2 ? parts.concat(parts) : parts;
}

export function fontString(style) {
  return (
    (style.fontStyle && style.fontStyle !== "normal"
      ? style.fontStyle + " "
      : "") +
    (style.fontWeight && style.fontWeight !== "normal"
      ? style.fontWeight + " "
      : "") +
    style.fontSize +
    "px " +
    style.fontFamily
  );
}

export { INHERITED_DEFAULTS };
